import type { RedListIndexSeriesId } from '@/constants/stateCharts';
import type {
  RedListIndexPoint,
  RedListIndexStats,
} from '@/content/data/charts/red-list-index';

/**
 * Red List Index — raw yearly series + side stats.
 *
 * Generated by `scripts/transform-red-list-index.mjs` from
 * `data/charts/red-list-index-line.csv`. Replace the CSV and re-run the
 * script to refresh the series; do not hand-edit `seriesByFilter` below.
 *
 * - index ← rli, lower ← qn05, upper ← qn95
 * - `all` is the aggregate series from the CSV (not a median of flyways)
 *   and supplies the All flyways line and summary statistics
 * - Side stats are product counts (not in the line CSV); edit
 *   `STATS_BY_FILTER` in the transform script to change them
 */
export const seriesByFilter: Record<
  RedListIndexSeriesId,
  readonly RedListIndexPoint[]
> = {
  'all': [
    { year: 1988, index: 0.940857298, lower: 0.940349069, upper: 0.941622558 },
    { year: 1989, index: 0.940703563, lower: 0.940151926, upper: 0.941214793 },
    { year: 1990, index: 0.940396093, lower: 0.939951167, upper: 0.940965817 },
    { year: 1991, index: 0.940260445, lower: 0.939734129, upper: 0.940730693 },
    { year: 1992, index: 0.939989148, lower: 0.939498101, upper: 0.940495569 },
    { year: 1993, index: 0.939744981, lower: 0.939030566, upper: 0.940260445 },
    { year: 1994, index: 0.939120998, lower: 0.938540423, upper: 0.940025321 },
    { year: 1995, index: 0.939075782, lower: 0.93814433, upper: 0.939790197 },
    { year: 1996, index: 0.938686923, lower: 0.937746428, upper: 0.939555073 },
    { year: 1997, index: 0.93817146, lower: 0.937345813, upper: 0.939120998 },
    { year: 1998, index: 0.937818774, lower: 0.936950624, upper: 0.938686923 },
    { year: 1999, index: 0.937384699, lower: 0.936299512, upper: 0.938252849 },
    { year: 2000, index: 0.936950624, lower: 0.935702659, upper: 0.937818774 },
    { year: 2001, index: 0.936340206, lower: 0.935078676, upper: 0.937384699 },
    { year: 2002, index: 0.935811177, lower: 0.934666305, upper: 0.936950624 },
    { year: 2003, index: 0.935051546, lower: 0.934102008, upper: 0.936380901 },
    { year: 2004, index: 0.934671731, lower: 0.933749322, upper: 0.935811177 },
    { year: 2005, index: 0.934264786, lower: 0.933366793, upper: 0.935241454 },
    { year: 2006, index: 0.933803581, lower: 0.932935431, upper: 0.934671731 },
    { year: 2007, index: 0.933450895, lower: 0.932608519, upper: 0.934264786 },
    { year: 2008, index: 0.93304395, lower: 0.932392838, upper: 0.93385784 },
    { year: 2009, index: 0.932718394, lower: 0.932067282, upper: 0.933450895 },
    { year: 2010, index: 0.932392838, lower: 0.931848888, upper: 0.93304395 },
    { year: 2011, index: 0.932094411, lower: 0.931494845, upper: 0.932745524 },
    { year: 2012, index: 0.931850244, lower: 0.931199132, upper: 0.932447097 },
    { year: 2013, index: 0.931524688, lower: 0.931062127, upper: 0.932148671 },
    { year: 2014, index: 0.931172002, lower: 0.930759631, upper: 0.931850244 },
    { year: 2015, index: 0.931036354, lower: 0.930626696, upper: 0.931606077 },
    { year: 2016, index: 0.930765057, lower: 0.930436788, upper: 0.93136191 },
    { year: 2017, index: 0.930629409, lower: 0.930356755, upper: 0.931117743 },
    { year: 2018, index: 0.93049376, lower: 0.930113945, upper: 0.930873576 },
    { year: 2019, index: 0.930330982, lower: 0.93002713, upper: 0.930737927 },
    { year: 2020, index: 0.930222463, lower: 0.929868421, upper: 0.930602279 },
    { year: 2021, index: 0.930113945, lower: 0.929734129, upper: 0.93046663 },
    { year: 2022, index: 0.929924037, lower: 0.929597124, upper: 0.930330982 },
    { year: 2023, index: 0.929788388, lower: 0.929137276, upper: 0.930195334 },
    { year: 2024, index: 0.929639175, lower: 0.928665331, upper: 0.930059685 },
    { year: 2025, index: 0.929354314, lower: 0.928203338, upper: 0.929898264 },
  ],
  'americas': [
    { year: 1988, index: 0.9601952, lower: 0.960193833, upper: 0.960196843 },
    { year: 1989, index: 0.960194517, lower: 0.96019315, upper: 0.960195995 },
    { year: 1990, index: 0.960193833, lower: 0.960192467, upper: 0.9601952 },
    { year: 1991, index: 0.96019315, lower: 0.960191783, upper: 0.960194517 },
    { year: 1992, index: 0.960192467, lower: 0.9601911, upper: 0.960193833 },
    { year: 1993, index: 0.960191783, lower: 0.960040783, upper: 0.96019315 },
    { year: 1994, index: 0.9601911, lower: 0.959890467, upper: 0.960192467 },
    { year: 1995, index: 0.960040783, lower: 0.95974015, upper: 0.960191783 },
    { year: 1996, index: 0.959890467, lower: 0.959589833, upper: 0.9601911 },
    { year: 1997, index: 0.95974015, lower: 0.959439517, upper: 0.960040783 },
    { year: 1998, index: 0.959514675, lower: 0.9592892, upper: 0.959890467 },
    { year: 1999, index: 0.959439517, lower: 0.95888355, upper: 0.95974015 },
    { year: 2000, index: 0.9592892, lower: 0.9584779, upper: 0.959589833 },
    { year: 2001, index: 0.9584779, lower: 0.95807225, upper: 0.959439517 },
    { year: 2002, index: 0.9584779, lower: 0.9576666, upper: 0.9592892 },
    { year: 2003, index: 0.95807225, lower: 0.95734215, upper: 0.95888355 },
    { year: 2004, index: 0.9576666, lower: 0.9570177, upper: 0.9584779 },
    { year: 2005, index: 0.95734215, lower: 0.95669325, upper: 0.95807225 },
    { year: 2006, index: 0.9570177, lower: 0.9563688, upper: 0.9576666 },
    { year: 2007, index: 0.95669325, lower: 0.9563688, upper: 0.95734215 },
    { year: 2008, index: 0.95669325, lower: 0.9563688, upper: 0.9570177 },
    { year: 2009, index: 0.95669325, lower: 0.9563688, upper: 0.957333075 },
    { year: 2010, index: 0.95701165, lower: 0.9563688, upper: 0.9576545 },
    { year: 2011, index: 0.957333075, lower: 0.956690225, upper: 0.9576545 },
    { year: 2012, index: 0.9575661, lower: 0.95701165, upper: 0.9576545 },
    { year: 2013, index: 0.9575661, lower: 0.957333075, upper: 0.9576545 },
    { year: 2014, index: 0.9575661, lower: 0.9574777, upper: 0.9576545 },
    { year: 2015, index: 0.9575219, lower: 0.957432625, upper: 0.9576103 },
    { year: 2016, index: 0.9574777, lower: 0.95738755, upper: 0.9575661 },
    { year: 2017, index: 0.957432625, lower: 0.957342475, upper: 0.9575219 },
    { year: 2018, index: 0.95738755, lower: 0.9572974, upper: 0.9574777 },
    { year: 2019, index: 0.957342475, lower: 0.95720725, upper: 0.957432625 },
    { year: 2020, index: 0.9572974, lower: 0.9571171, upper: 0.95738755 },
    { year: 2021, index: 0.95720725, lower: 0.95702695, upper: 0.957342475 },
    { year: 2022, index: 0.9571171, lower: 0.9569368, upper: 0.9572974 },
    { year: 2023, index: 0.95702695, lower: 0.9565762, upper: 0.95720725 },
    { year: 2024, index: 0.9569368, lower: 0.955984026, upper: 0.9571171 },
    { year: 2025, index: 0.9565762, lower: 0.955277516, upper: 0.95702695 },
  ],
  'african-eurasian': [
    { year: 1988, index: 0.9649482, lower: 0.964415167, upper: 0.965726698 },
    { year: 1989, index: 0.964681683, lower: 0.96414865, upper: 0.965337303 },
    { year: 1990, index: 0.964415167, lower: 0.963882133, upper: 0.9649482 },
    { year: 1991, index: 0.96414865, lower: 0.963615617, upper: 0.964681683 },
    { year: 1992, index: 0.963882133, lower: 0.9633491, upper: 0.964415167 },
    { year: 1993, index: 0.963615617, lower: 0.96234575, upper: 0.96414865 },
    { year: 1994, index: 0.9633491, lower: 0.9613424, upper: 0.963882133 },
    { year: 1995, index: 0.96234575, lower: 0.96033905, upper: 0.963615617 },
    { year: 1996, index: 0.9613424, lower: 0.9593357, upper: 0.9633491 },
    { year: 1997, index: 0.96033905, lower: 0.95833235, upper: 0.96234575 },
    { year: 1998, index: 0.9593357, lower: 0.957329, upper: 0.9613424 },
    { year: 1999, index: 0.95833235, lower: 0.956222925, upper: 0.96033905 },
    { year: 2000, index: 0.957329, lower: 0.95511685, upper: 0.9593357 },
    { year: 2001, index: 0.956222925, lower: 0.954010775, upper: 0.95833235 },
    { year: 2002, index: 0.95511685, lower: 0.9529047, upper: 0.957329 },
    { year: 2003, index: 0.954010775, lower: 0.952592225, upper: 0.956222925 },
    { year: 2004, index: 0.9529047, lower: 0.95227975, upper: 0.95511685 },
    { year: 2005, index: 0.952592225, lower: 0.951967275, upper: 0.954010775 },
    { year: 2006, index: 0.95227975, lower: 0.9516548, upper: 0.9529047 },
    { year: 2007, index: 0.951967275, lower: 0.950688425, upper: 0.952592225 },
    { year: 2008, index: 0.9516548, lower: 0.94972205, upper: 0.95227975 },
    { year: 2009, index: 0.950688425, lower: 0.948755675, upper: 0.951967275 },
    { year: 2010, index: 0.94972205, lower: 0.9477893, upper: 0.9516548 },
    { year: 2011, index: 0.94843545, lower: 0.9477893, upper: 0.950688425 },
    { year: 2012, index: 0.94844115, lower: 0.9477893, upper: 0.94972205 },
    { year: 2013, index: 0.94844115, lower: 0.9477893, upper: 0.948767075 },
    { year: 2014, index: 0.948767075, lower: 0.9477893, upper: 0.949093 },
    { year: 2015, index: 0.948767075, lower: 0.948115225, upper: 0.949093 },
    { year: 2016, index: 0.949093, lower: 0.94844115, upper: 0.949093 },
    { year: 2017, index: 0.949093, lower: 0.948767075, upper: 0.949093 },
    { year: 2018, index: 0.949093, lower: 0.949093, upper: 0.949093 },
    { year: 2019, index: 0.949093, lower: 0.9487719, upper: 0.949093 },
    { year: 2020, index: 0.949093, lower: 0.9484508, upper: 0.949093 },
    { year: 2021, index: 0.9487719, lower: 0.9481297, upper: 0.949093 },
    { year: 2022, index: 0.9484508, lower: 0.9478086, upper: 0.949093 },
    { year: 2023, index: 0.9481297, lower: 0.9478086, upper: 0.9487719 },
    { year: 2024, index: 0.9478086, lower: 0.9478086, upper: 0.9484508 },
    { year: 2025, index: 0.9478086, lower: 0.9478086, upper: 0.9481297 },
  ],
  'central-asia': [
    { year: 1988, index: 0.9540462, lower: 0.953729333, upper: 0.954471028 },
    { year: 1989, index: 0.953887767, lower: 0.9535709, upper: 0.954276705 },
    { year: 1990, index: 0.953729333, lower: 0.953412467, upper: 0.9540462 },
    { year: 1991, index: 0.9535709, lower: 0.953254033, upper: 0.953887767 },
    { year: 1992, index: 0.953491683, lower: 0.9530956, upper: 0.953729333 },
    { year: 1993, index: 0.953254033, lower: 0.9526613, upper: 0.9535709 },
    { year: 1994, index: 0.9530956, lower: 0.952227, upper: 0.953412467 },
    { year: 1995, index: 0.9526613, lower: 0.9517927, upper: 0.953254033 },
    { year: 1996, index: 0.952227, lower: 0.9513584, upper: 0.9530956 },
    { year: 1997, index: 0.9517927, lower: 0.9509241, upper: 0.9526613 },
    { year: 1998, index: 0.9513584, lower: 0.9504898, upper: 0.952227 },
    { year: 1999, index: 0.9509241, lower: 0.9492472, upper: 0.9517927 },
    { year: 2000, index: 0.9504898, lower: 0.9480046, upper: 0.9513584 },
    { year: 2001, index: 0.9492472, lower: 0.946762, upper: 0.9509241 },
    { year: 2002, index: 0.9480046, lower: 0.9455194, upper: 0.9504898 },
    { year: 2003, index: 0.946762, lower: 0.944547375, upper: 0.9492472 },
    { year: 2004, index: 0.9455194, lower: 0.94357535, upper: 0.9480046 },
    { year: 2005, index: 0.944547375, lower: 0.942603325, upper: 0.946762 },
    { year: 2006, index: 0.94357535, lower: 0.9416313, upper: 0.9455194 },
    { year: 2007, index: 0.942603325, lower: 0.941104325, upper: 0.944547375 },
    { year: 2008, index: 0.9416313, lower: 0.94057735, upper: 0.94357535 },
    { year: 2009, index: 0.941104325, lower: 0.940050375, upper: 0.942603325 },
    { year: 2010, index: 0.94057735, lower: 0.9395234, upper: 0.9416313 },
    { year: 2011, index: 0.940050375, lower: 0.939194475, upper: 0.941104325 },
    { year: 2012, index: 0.9395234, lower: 0.93886555, upper: 0.94057735 },
    { year: 2013, index: 0.939194475, lower: 0.938536625, upper: 0.940050375 },
    { year: 2014, index: 0.93886555, lower: 0.9382077, upper: 0.9395234 },
    { year: 2015, index: 0.938536625, lower: 0.93804485, upper: 0.939194475 },
    { year: 2016, index: 0.9382077, lower: 0.937882, upper: 0.93886555 },
    { year: 2017, index: 0.93804485, lower: 0.93771915, upper: 0.938536625 },
    { year: 2018, index: 0.937882, lower: 0.9375563, upper: 0.9382077 },
    { year: 2019, index: 0.93771915, lower: 0.9374006, upper: 0.93804485 },
    { year: 2020, index: 0.9375563, lower: 0.9372449, upper: 0.937882 },
    { year: 2021, index: 0.9374006, lower: 0.9370892, upper: 0.93771915 },
    { year: 2022, index: 0.9372449, lower: 0.9369335, upper: 0.9375563 },
    { year: 2023, index: 0.9370892, lower: 0.9369335, upper: 0.9374006 },
    { year: 2024, index: 0.9369335, lower: 0.9369335, upper: 0.9372449 },
    { year: 2025, index: 0.9369335, lower: 0.9369335, upper: 0.9370892 },
  ],
  'east-asia-australasia': [
    { year: 1988, index: 0.9460604, lower: 0.945145367, upper: 0.947166887 },
    { year: 1989, index: 0.945602883, lower: 0.94468785, upper: 0.946607223 },
    { year: 1990, index: 0.945145367, lower: 0.944230333, upper: 0.9460604 },
    { year: 1991, index: 0.94468785, lower: 0.943772817, upper: 0.945602883 },
    { year: 1992, index: 0.944230333, lower: 0.9433153, upper: 0.945145367 },
    { year: 1993, index: 0.943772817, lower: 0.942843233, upper: 0.94468785 },
    { year: 1994, index: 0.9433153, lower: 0.942371167, upper: 0.944230333 },
    { year: 1995, index: 0.942843233, lower: 0.9418991, upper: 0.943772817 },
    { year: 1996, index: 0.942371167, lower: 0.941427033, upper: 0.9433153 },
    { year: 1997, index: 0.9418991, lower: 0.940954967, upper: 0.942843233 },
    { year: 1998, index: 0.941427033, lower: 0.9404829, upper: 0.942371167 },
    { year: 1999, index: 0.941427033, lower: 0.94030405, upper: 0.9418991 },
    { year: 2000, index: 0.9404829, lower: 0.9401252, upper: 0.941427033 },
    { year: 2001, index: 0.94030405, lower: 0.93994635, upper: 0.940954967 },
    { year: 2002, index: 0.9401252, lower: 0.9397675, upper: 0.9404829 },
    { year: 2003, index: 0.93994635, lower: 0.93882895, upper: 0.94030405 },
    { year: 2004, index: 0.9397675, lower: 0.9378904, upper: 0.9401252 },
    { year: 2005, index: 0.93882895, lower: 0.93695185, upper: 0.93994635 },
    { year: 2006, index: 0.9378904, lower: 0.9360133, upper: 0.9397675 },
    { year: 2007, index: 0.93695185, lower: 0.935287575, upper: 0.93882895 },
    { year: 2008, index: 0.9360133, lower: 0.93456185, upper: 0.9378904 },
    { year: 2009, index: 0.935287575, lower: 0.933836125, upper: 0.93695185 },
    { year: 2010, index: 0.93456185, lower: 0.9331104, upper: 0.9360133 },
    { year: 2011, index: 0.933836125, lower: 0.932851525, upper: 0.935287575 },
    { year: 2012, index: 0.9331104, lower: 0.93259265, upper: 0.93456185 },
    { year: 2013, index: 0.932980963, lower: 0.932333775, upper: 0.933836125 },
    { year: 2014, index: 0.93259265, lower: 0.9320749, upper: 0.9331104 },
    { year: 2015, index: 0.932333775, lower: 0.93171345, upper: 0.932851525 },
    { year: 2016, index: 0.9320749, lower: 0.931352, upper: 0.93259265 },
    { year: 2017, index: 0.93171345, lower: 0.93099055, upper: 0.932333775 },
    { year: 2018, index: 0.931352, lower: 0.9306291, upper: 0.9320749 },
    { year: 2019, index: 0.93084795, lower: 0.9306291, upper: 0.93171345 },
    { year: 2020, index: 0.9307816, lower: 0.9306291, upper: 0.931352 },
    { year: 2021, index: 0.9307816, lower: 0.9306291, upper: 0.93099055 },
    { year: 2022, index: 0.9307816, lower: 0.9306291, upper: 0.9309341 },
    { year: 2023, index: 0.9307816, lower: 0.929104, upper: 0.9309341 },
    { year: 2024, index: 0.9307816, lower: 0.926520009, upper: 0.9309341 },
    { year: 2025, index: 0.929104, lower: 0.92524208, upper: 0.9309341 },
  ],
  'marine': [
    { year: 1988, index: 0.763279117, lower: 0.760647963, upper: 0.767393265 },
    { year: 1989, index: 0.7625567, lower: 0.75974415, upper: 0.765689598 },
    { year: 1990, index: 0.761655975, lower: 0.759446997, upper: 0.7645789 },
    { year: 1991, index: 0.7604918, lower: 0.757363567, upper: 0.76330435 },
    { year: 1992, index: 0.760081183, lower: 0.7569316, upper: 0.762703867 },
    { year: 1993, index: 0.7589538, lower: 0.756170778, upper: 0.7612608 },
    { year: 1994, index: 0.758917008, lower: 0.756518728, upper: 0.760828833 },
    { year: 1995, index: 0.75877575, lower: 0.756170778, upper: 0.76011435 },
    { year: 1996, index: 0.758691492, lower: 0.75676828, upper: 0.7605012 },
    { year: 1997, index: 0.759153067, lower: 0.7568129, upper: 0.7605012 },
    { year: 1998, index: 0.75954945, lower: 0.757462918, upper: 0.7612749 },
    { year: 1999, index: 0.759767783, lower: 0.757804933, upper: 0.7612749 },
    { year: 2000, index: 0.759274, lower: 0.756987428, upper: 0.7612749 },
    { year: 2001, index: 0.759274, lower: 0.756030525, upper: 0.7612749 },
    { year: 2002, index: 0.7575471, lower: 0.7532713, upper: 0.7612749 },
    { year: 2003, index: 0.7568233, lower: 0.7542824, upper: 0.759273976 },
    { year: 2004, index: 0.7563045, lower: 0.753283864, upper: 0.758309475 },
    { year: 2005, index: 0.7563045, lower: 0.7542824, upper: 0.757550153 },
    { year: 2006, index: 0.7563045, lower: 0.7532713, upper: 0.7583797 },
    { year: 2007, index: 0.7568233, lower: 0.754788536, upper: 0.7583797 },
    { year: 2008, index: 0.75687345, lower: 0.755295285, upper: 0.7583901 },
    { year: 2009, index: 0.7573738, lower: 0.755069463, upper: 0.7584005 },
    { year: 2010, index: 0.7574102, lower: 0.755322683, upper: 0.7584213 },
    { year: 2011, index: 0.757244, lower: 0.754703636, upper: 0.7584213 },
    { year: 2012, index: 0.7563887, lower: 0.754202414, upper: 0.7584213 },
    { year: 2013, index: 0.75572735, lower: 0.753012503, upper: 0.7584213 },
    { year: 2014, index: 0.754929213, lower: 0.7506789, upper: 0.7584213 },
    { year: 2015, index: 0.75404455, lower: 0.7506789, upper: 0.757244 },
    { year: 2016, index: 0.7532066, lower: 0.751317039, upper: 0.755055603 },
    { year: 2017, index: 0.75270105, lower: 0.7506789, upper: 0.754409128 },
    { year: 2018, index: 0.752701038, lower: 0.7506789, upper: 0.7537121 },
    { year: 2019, index: 0.752701075, lower: 0.7506789, upper: 0.7537121 },
    { year: 2020, index: 0.752195538, lower: 0.7498521, upper: 0.7537121 },
    { year: 2021, index: 0.75169, lower: 0.749755343, upper: 0.7537121 },
    { year: 2022, index: 0.75136875, lower: 0.7480142, upper: 0.7537121 },
    { year: 2023, index: 0.750909263, lower: 0.7470031, upper: 0.7525404 },
    { year: 2024, index: 0.75086315, lower: 0.746919568, upper: 0.7523798 },
    { year: 2025, index: 0.749990325, lower: 0.745440857, upper: 0.752138513 },
  ],
};

/** Per-filter side stats for Red List Index flanking pods. */
export const statsByFlyway: Record<RedListIndexSeriesId, RedListIndexStats> = {
  'all': { improvedCount: 34, deterioratedCount: 102 },
  'americas': { improvedCount: 11, deterioratedCount: 26 },
  'african-eurasian': { improvedCount: 11, deterioratedCount: 35 },
  'central-asia': { improvedCount: 6, deterioratedCount: 28 },
  'east-asia-australasia': { improvedCount: 9, deterioratedCount: 40 },
  'marine': { improvedCount: 4, deterioratedCount: 11 },
};
